import api from './api'

interface ElasticsearchItem {
	id: string
	name: string
	TITLE?: string
	NAME?: string
	bitrixId?: string
	description?: string
	score?: number
}

interface ElasticsearchResponse {
	success?: boolean
	result?: ElasticsearchItem[]
	data?: ElasticsearchItem[]
	total: number
	query: string
	searchEngine: string
}

class ElasticsearchService {
	private cache = new Map<string, { data: any; timestamp: number }>()
	private readonly CACHE_TTL = 5 * 60 * 1000 // 5 минут

	/**
	 * Получение названия компании по ID
	 */
	async getCompanyNameById(companyId: string): Promise<string | null> {
		const cacheKey = `company_${companyId}`
		const cached = this.getCachedResult(cacheKey)
		if (cached) {
			return cached
		}

		try {
			console.log(
				`[ElasticsearchService] Запрос названия компании для ID: ${companyId}`
			)
			const response = await api.post<ElasticsearchResponse>(
				'/api/search/companies',
				{
					query: companyId,
					limit: 1,
					offset: 0,
					fuzzy: false,
				}
			)

			console.log(
				`[ElasticsearchService] Ответ для компании ${companyId}:`,
				response.data
			)
			const results = response.data.result || response.data.data || []
			if (results.length > 0) {
				const company = results[0]
				const name = company.TITLE || company.name || companyId
				console.log(
					`[ElasticsearchService] Найдено название для ${companyId}: ${name}`
				)
				this.setCachedResult(cacheKey, name)
				return name
			}

			// Если не найдено в Elasticsearch, возвращаем ID
			this.setCachedResult(cacheKey, companyId)
			return companyId
		} catch (error) {
			console.error('Ошибка получения названия компании:', error)
			return companyId
		}
	}

	/**
	 * Получение названия бетона по ID
	 */
	async getProductNameById(productId: string): Promise<string | null> {
		const cacheKey = `product_${productId}`
		const cached = this.getCachedResult(cacheKey)
		if (cached) {
			return cached
		}

		try {
			const response = await api.post<ElasticsearchResponse>(
				'/api/search/products',
				{
					query: productId,
					limit: 1,
					offset: 0,
					fuzzy: false,
				}
			)

			const results = response.data.result || response.data.data || []
			if (results.length > 0) {
				const product = results[0]
				const name = product.NAME || product.name || productId
				this.setCachedResult(cacheKey, name)
				return name
			}

			// Если не найдено в Elasticsearch, возвращаем ID
			this.setCachedResult(cacheKey, productId)
			return productId
		} catch (error) {
			console.error('Ошибка получения названия продукта:', error)
			return productId
		}
	}

	/**
	 * Разрешение названий пачкой одним запросом.
	 * Раньше на каждый ID уходил отдельный поиск — на странице списка это были
	 * два десятка запросов; здесь всё сводится к одному обращению.
	 */
	private async resolveNames(
		kind: 'company' | 'product',
		ids: string[]
	): Promise<Record<string, string>> {
		const result: Record<string, string> = {}
		const uncachedIds: string[] = []

		for (const id of ids) {
			const cached = this.getCachedResult(`${kind}_${id}`)
			if (cached) {
				result[id] = cached
			} else if (!uncachedIds.includes(id)) {
				uncachedIds.push(id)
			}
		}

		if (uncachedIds.length === 0) {
			return result
		}

		try {
			const payload =
				kind === 'company'
					? { companyIds: uncachedIds }
					: { productIds: uncachedIds }
			const response = await api.post<{
				success: boolean
				data: {
					companies: Record<string, string>
					products: Record<string, string>
				}
			}>('/api/search/names', payload)

			const names =
				(kind === 'company'
					? response.data?.data?.companies
					: response.data?.data?.products) || {}

			for (const id of uncachedIds) {
				const name = names[id] || id
				this.setCachedResult(`${kind}_${id}`, name)
				result[id] = name
			}
		} catch (error) {
			console.error(
				`Ошибка получения названий (${kind}), показываем идентификаторы:`,
				error
			)
			for (const id of uncachedIds) {
				result[id] = id
			}
		}

		return result
	}

	/**
	 * Получение названий компаний по массиву ID
	 */
	async getCompanyNamesByIds(
		companyIds: string[]
	): Promise<Record<string, string>> {
		return this.resolveNames('company', companyIds)
	}

	/**
	 * Получение названий продуктов по массиву ID
	 */
	async getProductNamesByIds(
		productIds: string[]
	): Promise<Record<string, string>> {
		return this.resolveNames('product', productIds)
	}

	/**
	 * Очистка кэша
	 */
	clearCache(): void {
		this.cache.clear()
	}

	/**
	 * Получение данных из кэша
	 */
	private getCachedResult(key: string): string | null {
		const cached = this.cache.get(key)
		if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
			return cached.data
		}
		this.cache.delete(key)
		return null
	}

	/**
	 * Сохранение данных в кэш
	 */
	private setCachedResult(key: string, data: string): void {
		this.cache.set(key, { data, timestamp: Date.now() })

		// Очищаем старые записи если кэш стал слишком большим
		if (this.cache.size > 1000) {
			const now = Date.now()
			const entries = Array.from(this.cache.entries())
			for (const [k, v] of entries) {
				if (now - v.timestamp > this.CACHE_TTL) {
					this.cache.delete(k)
				}
			}
		}
	}
}

export const elasticsearchService = new ElasticsearchService()
export default elasticsearchService
